import { useMutation } from '@apollo/client';
import { GridApi } from '@mui/x-data-grid-pro';
import { useCustomSnackbar } from '@procureai/ui-mui';
import { MutableRefObject, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { TsrctDemandResponsibleUsersInput } from '@/generated/gql/graphql';
import { DemandUser } from '@/legacy/common/types/DemandUser';
import { NegotiationCaseDataGridRow } from '~/common/types';
import { UserSelectCellContext } from '~/components/DataGridCustom';
import { isString } from '~/util/string';

import { NEGOTIATION_LIST_UPDATE_RESPONSIBLE_MUTATION } from './NegotiationCaseListUpdateResponsible.gql';

type NegotiationCaseDataGridContext = UserSelectCellContext;
type DemandResponsible = DemandUser | string;

interface UseNegotiationCaseDataGridProps {
  apiRef: MutableRefObject<GridApi>;
}

export const useNegotiationCaseDataGrid = ({ apiRef }: UseNegotiationCaseDataGridProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'NegotiationCaseDataGrid.error' });
  const { enqueueErrorSnackbar } = useCustomSnackbar();

  const [updateUser] = useMutation(NEGOTIATION_LIST_UPDATE_RESPONSIBLE_MUTATION, { errorPolicy: 'all' });

  const contextValue: NegotiationCaseDataGridContext = useMemo(
    () => ({
      onUserChange: async (rowId, user) => {
        const row = apiRef.current.getRow<NegotiationCaseDataGridRow>(rowId);
        if (row) {
          let userId: string | null = null;
          const responsibleIds: DemandResponsible[] = [];
          if (user) {
            if (isString(user)) {
              userId = user;
              responsibleIds.push(userId);
            } else {
              userId = user.primarySourceReference;
              responsibleIds.push(userId);
            }
          }
          // update datagrid-row cache to reflect change immediately
          apiRef.current.updateRows([{ ...row, responsibleIds }]);

          const updateInput: TsrctDemandResponsibleUsersInput = {
            responsibleUserIds: userId ? [userId] : [],
          };
          // mutation to update entry in the db
          const { data, errors } = await updateUser({
            variables: {
              id: row.demandId,
              data: updateInput,
            },
            // update the apollo cache to reflect the change if the entity from cache is used
            update: (cache, result) => {
              cache.modify({
                id: cache.identify({
                  __typename: 'TsrctNegotiationCaseListItem',
                  id: row.id,
                }),
                fields: {
                  responsibleIds: () => {
                    return result.data?.tesseract.demand.responsibleUsers.update.responsibleUserIds;
                  },
                },
              });
            },
          });
          const updatedUserId = data?.tesseract?.demand.responsibleUsers.update.responsibleUserIds?.[0] || null;
          if (errors || updatedUserId !== userId) {
            // rollback the changes in the grid cache
            apiRef.current.updateRows([row]);
            enqueueErrorSnackbar(t('updateUser'));
          }
        }
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t],
  );

  return {
    contextValue,
  };
};
